import { Component, computed, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { CurrentLangService } from '@core/services/common/current-lang.service';
import { FindingCopyService } from '@core/services/finding-copy/finding-copy.service';
import {
  DistinctRef,
  distinctRefs,
  groupRefsByRelation,
  relationLabel
} from '@core/services/gbe-error-parser/entity-ref-labels';

import { AnchoredPopupComponent } from '@shared/components/anchored-popup/anchored-popup.component';
import { ReferencePopupService } from '@shared/services/reference-popup/reference-popup.service';

import { FindingCardPopupService } from './finding-card-popup.service';
import {
  FindingCardSection,
  PlanFindingCardComponent
} from './plan-finding-card.component';

/**
 * Smart outlet mounted once in the app shell: reads `FindingCardPopupService`'s state,
 * resolves the open occurrence's references into translated per-role sections, and
 * renders the dumb card inside the shared anchored-popup shell.
 */
@Component({
  selector: 'app-plan-finding-card-outlet',
  imports: [AnchoredPopupComponent, PlanFindingCardComponent],
  template: `
    @if (state(); as s) {
      <app-anchored-popup
        [anchorEl]="s.anchorEl"
        [width]="340"
        [owner]="popupService"
        (dismiss)="popupService.close()">
        <app-plan-finding-card
          [findingTitle]="s.data.title"
          [code]="s.data.code"
          [body]="s.data.occurrence.body"
          [sections]="sections()"
          (openReference)="onOpenReference($event)"
          (copyRequested)="onCopy($event)"
          (dismiss)="popupService.close()" />
      </app-anchored-popup>
    }
  `,
  styles: ``
})
export class PlanFindingCardOutletComponent {
  protected readonly popupService = inject(FindingCardPopupService);
  private readonly referencePopup = inject(ReferencePopupService);
  private readonly findingCopy = inject(FindingCopyService);
  private readonly translate = inject(TranslateService);
  private readonly currentLangService = inject(CurrentLangService);

  protected readonly state = this.popupService.state;

  protected readonly sections = computed<FindingCardSection[]>(() => {
    this.currentLangService.currentLang();
    const open = this.state();
    if (!open) return [];
    const refs = distinctRefs(
      open.data.occurrence.refs,
      this.translate,
      open.data.typeLabels
    );
    return groupRefsByRelation(refs).map((group) => ({
      heading: this.translate.instant(
        `features.plan.warnings.relation_group.${group.relation}`
      ),
      refs: group.refs.map((ref) => ({
        ref,
        alsoLabel: this.alsoLabel(ref),
        typeLabel: open.data.typeLabels.get(ref.entityType) ?? ref.entityType
      }))
    }));
  });

  /**
   * An entity is listed under its first role only, so the roles it also holds would
   * otherwise be lost — and holding two of them is the case a reader most needs told.
   */
  private alsoLabel(ref: DistinctRef): string | null {
    const others = ref.relations.slice(1);
    if (others.length === 0) return null;
    return this.translate.instant('features.plan.warnings.also_roles', {
      roles: others.map((relation) => relationLabel(relation, this.translate)).join(', ')
    });
  }

  protected onOpenReference(event: { ref: DistinctRef; anchorEl: HTMLElement }): void {
    if (event.ref.uid === null) return;
    this.referencePopup.open(event.anchorEl, {
      uid: event.ref.uid,
      targetEntityType: event.ref.entityType,
      column: '',
      row: undefined
    });
  }

  protected onCopy(rawPayload: boolean): void {
    const open = this.state();
    if (!open) return;
    this.findingCopy.copy(open.data.occurrence, open.data.raw, rawPayload);
  }
}
